import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, patientsAPI, formsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatRelativeTime, getCategoryColor } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Users,
  FileText,
  ClipboardList,
  UserPlus,
  Activity,
  TrendingUp,
  ArrowRight,
  Calendar,
  Plus,
  Stethoscope,
  Pill,
  TestTube,
} from 'lucide-react';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, patientsRes] = await Promise.all([
          dashboardAPI.getStats(),
          patientsAPI.getAll({ limit: 5 }),
        ]);
        setStats(statsRes.data);
        setRecentPatients(patientsRes.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const statCards = [
    {
      title: 'Total Patients',
      value: stats?.patients || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Form Submissions',
      value: stats?.submissions || 0,
      icon: FileText,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Form Templates',
      value: stats?.templates || 0,
      icon: ClipboardList,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: "Today's Activity",
      value: stats?.today_submissions || 0,
      icon: Activity,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
  ];

  const quickActions = [
    { label: 'New Patient', icon: UserPlus, href: '/patients/new', permission: 'patients:create' },
    { label: 'Consultation', icon: Stethoscope, href: '/forms?category=consultation', permission: 'submissions:create' },
    { label: 'Prescription', icon: Pill, href: '/forms?category=prescription', permission: 'submissions:create' },
    { label: 'Lab Order', icon: TestTube, href: '/forms?category=lab', permission: 'submissions:create' },
  ];

  const getCategoryIcon = (category) => {
    const icons = {
      consultation: Stethoscope,
      prescription: Pill,
      lab: TestTube,
      progress: FileText,
    };
    return icons[category] || FileText;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Welcome back, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's what's happening in your practice today
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={stat.title} className="stat-card card-interactive" data-testid={`stat-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold mt-2">{stat.value}</p>
                </div>
                <div className={cn('p-3 rounded-xl', stat.bgColor)}>
                  <stat.icon className={cn('h-6 w-6', stat.color)} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Quick Actions</CardTitle>
          <CardDescription>Common tasks at your fingertips</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {quickActions
              .filter(action => hasPermission(action.permission))
              .map((action) => (
                <Button
                  key={action.label}
                  variant="outline"
                  className="gap-2 hover:bg-primary hover:text-primary-foreground transition-colors"
                  onClick={() => navigate(action.href)}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                >
                  <action.icon className="h-4 w-4" />
                  {action.label}
                </Button>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Patients */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-lg">Recent Patients</CardTitle>
              <CardDescription>Latest patient records</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="gap-1"
              onClick={() => navigate('/patients')}
              data-testid="view-all-patients"
            >
              View all
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              <div className="space-y-3">
                {recentPatients.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
                    <p>No patients yet</p>
                    {hasPermission('patients:create') && (
                      <Button 
                        variant="link" 
                        className="mt-2"
                        onClick={() => navigate('/patients/new')}
                      >
                        Add your first patient
                      </Button>
                    )}
                  </div>
                ) : (
                  recentPatients.map((patient) => (
                    <div
                      key={patient.id}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => navigate(`/patients/${patient.id}`)}
                      data-testid={`patient-row-${patient.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="font-semibold text-sm text-primary">
                          {patient.first_name[0]}{patient.last_name[0]}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {patient.first_name} {patient.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {patient.patient_id}
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {patient.gender}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-lg">Recent Activity</CardTitle>
              <CardDescription>Latest form submissions</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="gap-1"
              onClick={() => navigate('/submissions')}
              data-testid="view-all-submissions"
            >
              View all
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              <div className="space-y-3">
                {stats?.recent_submissions?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
                    <p>No submissions yet</p>
                  </div>
                ) : (
                  stats?.recent_submissions?.map((submission) => {
                    const IconComponent = getCategoryIcon(submission.category);
                    return (
                      <div
                        key={submission.id}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors"
                        data-testid={`submission-row-${submission.id}`}
                      >
                        <div className={cn(
                          'w-10 h-10 rounded-full flex items-center justify-center',
                          getCategoryColor(submission.category)
                        )}>
                          <IconComponent className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{submission.form_name}</p>
                          <p className="text-sm text-muted-foreground">
                            by {submission.submitted_by_name}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(submission.created_at)}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
